import React from "react";
import {Button, Form} from "react-bootstrap";
import {
    ColumnDef,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    getFilteredRowModel,
    useReactTable,
} from "@tanstack/react-table";
import "../GenGenPlate/RawData/GenGenPlateCalculatedResult.css"
import {TableColumnFormatter} from "../../CommonFunc/TableColumnFormatter";
import {saveAs} from "file-saver";

interface TanStackTableProps {
    data: any[]
    columns: ColumnDef<any, any>[]
    tableName: string
    keyField: string
    rowClasses: ((row: any, rowIndex: number) => string)
    noDataComponent: React.ReactNode
}

// Extend base @tanstack/react-table to include custom meta field
declare module '@tanstack/react-table' {
    interface ColumnMeta<TData extends unknown, TValue> {
        hidden?: boolean
        width?: string
        definition?: string
    }
}

export const TanStackTableWithUpdate: React.FC<TanStackTableProps> = ({
    data,
    columns,
    tableName,
    keyField,
    rowClasses,
    noDataComponent,
}) => {
    const [globalFilter, setGlobalFilter] = React.useState('')
    const [pageSize, setPageSize] = React.useState(10)
    const [pageIndex, setPageIndex] = React.useState(0)

    const columnVisibility: Record<string, boolean> = {};

    columns.forEach(col => {
        const key = col.id as string;
        columnVisibility[key] = !col.meta?.hidden;
    });

    const table = useReactTable({
        data,
        columns,
        state: {
            globalFilter,
            columnVisibility,
            pagination: {
                pageIndex,
                pageSize,
            },
        },
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: (updater) => {
            const next = typeof updater === "function" ? updater({ pageIndex, pageSize }) : updater
            setPageIndex(next.pageIndex)
            setPageSize(next.pageSize)
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        enableSorting: true,
        manualPagination: false,
        getRowId: (row) => row[keyField],
    })

    const paginationSize = [10, 15, 20, 50, 100]
    const noData = table.getRowModel().rows.length === 0
    const visibleColumnCount = table.getHeaderGroups()[0]?.headers.filter(
        header => !header.column.columnDef.meta?.hidden
    ).length || 0
    const headerFontSize = visibleColumnCount > 15 ? '0.75rem' : '0.9rem'

    const handleExportCSV = () => {
        const visibleCols = table.getVisibleFlatColumns().map(col => col.id)
        const csvRows = [visibleCols.join(',')]

        table.getFilteredRowModel().rows.forEach(row => {
            const values = row.getVisibleCells().map(cell => JSON.stringify(cell.getValue() ?? ''))
            csvRows.push(values.join(','))
        })

        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8' })

        const now = new Date()
        const timestamp = now.toISOString().split('T')[0]

        saveAs(blob, `${tableName}_filtered_${timestamp}.csv`)
    }

    return (
        <div>
            <div className="mb-2 d-flex justify-content-between align-items-center">
                <Form.Control
                    value={globalFilter ?? ''}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder={`Filter ${data.length} search results`}
                    style={{maxWidth: '300px'}}/>
                <Button
                    className={"btn-success btn-sm float-end"}
                    variant={"success"}
                    size={"sm"}
                    onClick={handleExportCSV}
                >
                    Export filtered row
                </Button>
            </div>

            <div className="table-responsive">
                <table className="table table-hover table-bordered style={{tableLayout: 'fixed'}}">
                    <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                if (header.column.columnDef.meta?.hidden) return null
                                return (
                                    <th key={header.id}
                                        style={{
                                            cursor: header.column.getCanSort() ? 'pointer' : '',
                                            width: header.column.columnDef.meta?.width,
                                            minWidth: header.column.columnDef.meta?.width,
                                            textOverflow: 'ellipsis',
                                            fontSize: headerFontSize,
                                        }}
                                    >
                                        <div className='d-flex justify-content-between align-items-center w-100'>
                                            {header.isPlaceholder ? null :
                                                flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                            {header.column.getCanSort() && (
                                                <span className="ms-1"
                                                      onClick={header.column.getToggleSortingHandler()}
                                                      style={{
                                                          display: 'inline-block',
                                                          marginLeft: '4px',
                                                          cursor: 'pointer'
                                                      }}
                                                >
                                                {{
                                                    asc: <span style={{color: 'darkgreen'}}>▲</span>,
                                                    desc: <span style={{color: 'darkgreen'}}>▼</span>
                                                }[header.column.getIsSorted() as string] || '▼'}
                                            </span>
                                            )}
                                        </div>
                                    </th>
                            )
                            })}
                        </tr>
                    ))}
                    </thead>
                    <tbody>
                    {noData ? (
                        <tr>
                            <td colSpan={columns.length}>
                                {noDataComponent}
                            </td>
                        </tr>
                    ) : (
                        table.getRowModel().rows.map((row) => (
                            <tr key={row.id} className={rowClasses ? rowClasses(row.original, row.index) : ''}>
                                {row.getVisibleCells().map((cell, colIndex) => {
                                    const className = TableColumnFormatter.renderCssClasses(
                                        cell.getValue(),
                                        cell.row.original,
                                        cell.row.index,
                                        colIndex,
                                        cell.column.id,
                                        tableName,
                                        columns.length,
                                    )
                                    return <td className={`p-0 ${className} py-1 px-2`} key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                })}
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            <div className={"d-flex justify-content-between align-items-center mt-3"}>
                <div className={"d-flex gap-2"}>
                    <Form.Select
                        size={"sm"}
                        style={{width: "auto"}}
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                        {paginationSize.map((size) => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </Form.Select>
                    <span>
                        {`Showing rows ${
                            table.getFilteredRowModel().rows.length === 0 
                                ? 0 
                                : pageIndex * pageSize + 1
                        } to ${
                            Math.min((pageIndex + 1) * pageSize, table.getFilteredRowModel().rows.length)
                        } of ${table.getFilteredRowModel().rows.length}`}
                    </span>
                </div>

                <div className={"d-flex align-items-center gap-2"}>
                    <Button
                        onClick={() => table.setPageIndex(0)}
                        disabled={table.getState().pagination.pageIndex === 0}
                    >«</Button>
                    <Button
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >‹</Button>
                    <span>Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}</span>
                    <Button
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >›</Button>
                    <Button
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                    >»</Button>
                </div>
            </div>
        </div>
    )
}